import { lensPath, view, set, toPairs } from 'rambda';
import {
  UrlSearchState,
  Refinements,
  FacetFilterValue,
  UrlSearchStateValue,
  NumericFilterValue,
  NumericFilter,
  FacetFilter,
  Facet,
  FacetValuesList,
  FacetLayout,
  FacetValue,
  PriceSortQueryValue,
} from '@packages/common';

export const mapUrlSearchStateValueToFacetFilterValue =
  (facetValues: FacetValue[]) =>
  (urlSearchStateValue: UrlSearchStateValue): FacetFilterValue => {
    const { value, isHero } = urlSearchStateValue;
    const facetValue = facetValues && facetValues.find((item) => item.seoName === value);
    const { name, seoName, featureValue } = facetValue || { name: value, seoName: value, featureValue: '' };
    // Use url search state value name, if the facet value is not available
    // It can be either removed or it can be belong to a facet which is not loaded yet
    return {
      isHero,
      name,
      seoName,
      featureValue,
    };
  };

export const mapUrlSearchStateValueToNumericFilterValue = (
  urlSearchStateValue: UrlSearchStateValue,
): NumericFilterValue => {
  const [min = 0, max = 0] = urlSearchStateValue.value.split('-').map(Number);
  // TODO: If numeric filter value is invalid (like &f_preis=some-1000), we need to exclude
  // the whole facet, not just sanitize the value
  return {
    min: isNaN(min) ? 0 : min,
    max: isNaN(max) ? 0 : max,
  };
};

const mapUrlSearchStateToRefinements =
  (facets: Facet[], facetValuesList: FacetValuesList) =>
  (urlSearchState: UrlSearchState): Refinements => {
    const result = toPairs(urlSearchState).reduce(
      (refinements, [facetSeoName, values]) => {
        const facet = facets.find((item) => item.seoName === facetSeoName);

        // Bail if facet is not available
        if (!facet) {
          return refinements;
        }

        const { seoName, relation, unit } = facet;
        const facetValues = facetValuesList[facet.seoName];
        const isNumeric = facet.layout === FacetLayout.SLIDER;

        // create the lens based on facet layout type
        const lens = lensPath([isNumeric ? 'numericFilters' : 'facetFilters']);

        // set common properties for numeric and facet filters
        let result = {
          seoName,
          relation,
        };

        // fill the other properties based on filter type
        if (isNumeric) {
          result = {
            ...result,
            unit,
            // numeric filters can not have multiple values e.g &preis=100-2000
            // TODO: fix typings here
            value: mapUrlSearchStateValueToNumericFilterValue(values[0]),
          } as NumericFilter;
        } else {
          result = {
            ...result,
            // for facet filters values can be multiple e.g &farbe=blau.grun
            values: values.map(mapUrlSearchStateValueToFacetFilterValue(facetValues)),
          } as FacetFilter;
        }
        // update the accumulator
        return {
          ...set(lens, { ...view<any, any>(lens, refinements), [facet.featureMapping]: result }, refinements),
        };
      },
      {
        facetFilters: {},
        numericFilters: {},
      },
    );
    const { p = 0, ps = PriceSortQueryValue.NONE, query = '', local } = urlSearchState;
    return { ...result, page: p, priceSort: ps, query, local: local ? local : Boolean(local) } as Refinements;
  };

export default mapUrlSearchStateToRefinements;
